import { doNothing } from '@/utils/doNothing';

const MAX_CONCURRENT = 3;

interface QueuedTask {
  key: string;
  run: () => Promise<unknown>;
}

interface TranslationScheduler {
  schedule: (key: string, run: () => Promise<unknown>) => void;
  disable: () => void;
  isDisabled: () => boolean;
}

const createTranslationScheduler = (): TranslationScheduler => {
  let activeCount = 0;
  let isDisabled = false;
  let hasSettledOnce = false;
  const seen = new Set<string>();
  const queue: QueuedTask[] = [];

  // Until the first scheduled request settles, run a single probe. This keeps a
  // missing-plugin account (which 403s and trips the kill-switch) to one failed
  // request instead of MAX_CONCURRENT before disable() takes effect.
  const currentLimit = (): number => (hasSettledOnce ? MAX_CONCURRENT : 1);

  const pump = (): void => {
    while (!isDisabled && activeCount < currentLimit() && queue.length > 0) {
      const task = queue.shift();
      if (!task) {
        return;
      }
      activeCount += 1;
      Promise.resolve()
        .then(task.run)
        .catch(doNothing)
        .finally(() => {
          activeCount -= 1;
          hasSettledOnce = true;
          pump();
        });
    }
  };

  return {
    schedule: (key, run) => {
      if (isDisabled || seen.has(key)) {
        return;
      }
      seen.add(key);
      queue.push({ key, run });
      pump();
    },
    disable: () => {
      isDisabled = true;
      queue.length = 0;
    },
    isDisabled: () => isDisabled,
  };
};

let scheduler = createTranslationScheduler();

export const scheduleTranslation = (key: string, run: () => Promise<unknown>): void => scheduler.schedule(key, run);

export const disableTranslations = (): void => scheduler.disable();

export const isTranslationDisabled = (): boolean => scheduler.isDisabled();

export const resetTranslationScheduler = (): void => {
  scheduler = createTranslationScheduler();
};
